import React, { useState, useCallback } from 'react';
import { multiChoiceFilterValuesFrom } from '../multiChoiceFilterUtils';

/**
 * Hook to manage filtering state and logic for DataGrid
 * 
 * @param {boolean} enabled - Whether filtering is enabled
 * @param {Array} columns - Column definitions
 * @returns {Object} { filters, updateFilter, applyFilterValues, applyFilters }
 */
export function useDataGridFiltering(enabled, columns) {
  const [filters, setFilters] = useState({});
  const updateFilter = useCallback((columnKey, filterText) => {
    setFilters(prev => ({
      ...prev,
      [columnKey]: filterText
    }));
  }, []);
  const applyFilterValues = useCallback(filterValues => {
    setFilters(filterValues || {});
  }, []);
  const applyFilters = useCallback(rows => {
    if (!enabled) return rows;
    const activeFilters = Object.entries(filters).filter(_ref => {
      let [_, value] = _ref;
      return value !== null && value !== undefined && value.toString().trim() !== '';
    });
    if (activeFilters.length === 0) return rows;

    // Separate totals rows from filterable rows
    const totalsRows = rows.filter(row => row.isTotalsRow);
    const filterableRows = rows.filter(row => !row.isTotalsRow);
    const filteredRows = filterableRows.filter(row => {
      // Row must pass ALL active filters
      return activeFilters.every(_ref2 => {
        let [columnKey, filterText] = _ref2;
        const columnIndex = parseInt(columnKey.replace('col_', ''));
        const column = columns[columnIndex];
        if (!column?.valueFnForSortAndFilter) return true;

        // Get the value using the valueFnForSortAndFilter function
        const wrappedRow = {
          data: {
            object: row
          }
        };
        const value = column.valueFnForSortAndFilter(wrappedRow);

        // Case-insensitive text matching
        const valueStr = String(value).toLowerCase();
        const filterValues = multiChoiceFilterValuesFrom(filterText).map(filterValue => filterValue.toLowerCase());
        if (filterValues.length === 0) return true;
        return filterValues.some(filterValue => valueStr.includes(filterValue));
      });
    });

    // Put totals rows back at the beginning
    return [...totalsRows, ...filteredRows];
  }, [enabled, filters, columns]);
  return {
    filters,
    updateFilter,
    applyFilterValues,
    applyFilters
  };
}